import type { ApiErrorResponse } from '../types/api.types';

const BASE_URL = '/api';

export class ApiError extends Error {
  public readonly statusCode: number;
  public readonly status: string;
  public readonly errors?: unknown[];
  public readonly errorCode?: string;
  public readonly retryAfterSeconds?: number;

  constructor(response: ApiErrorResponse & { errors?: unknown[] }) {
    super(response.message);
    this.name = 'ApiError';
    this.statusCode = response.statusCode;
    this.status = response.status;
    this.errors = response.errors;
    this.errorCode = response.errorCode;
    this.retryAfterSeconds = response.retryAfterSeconds;
  }
}

function createGetAuthToken(storageKey: string) {
  return (): string | null => {
    try {
      const stored = localStorage.getItem(storageKey);
      if (!stored) return null;
      const parsed = JSON.parse(stored) as { state?: { accessToken?: string } };
      return parsed.state?.accessToken ?? null;
    } catch {
      return null;
    }
  };
}

export interface ApiRequestOptions extends RequestInit {
  skipAuth?: boolean;
}

async function request<T>(
  endpoint: string,
  getAuthToken: () => string | null,
  options: ApiRequestOptions = {},
): Promise<T> {
  const url = `${BASE_URL}${endpoint}`;
  const { skipAuth, ...fetchOptions } = options;
  const headers = new Headers(fetchOptions.headers);

  if (!headers.has('Content-Type') && fetchOptions.body) {
    headers.set('Content-Type', 'application/json');
  }

  const token = skipAuth ? null : getAuthToken();
  if (token) {
    headers.set('Authorization', `Bearer ${token}`);
  }

  const response = await fetch(url, {
    ...fetchOptions,
    headers,
  });

  const data: unknown = await response.json();

  if (!response.ok) {
    const body = (data ?? {}) as Partial<ApiErrorResponse> & { errors?: unknown[] };
    throw new ApiError({
      statusCode: response.status,
      status: 'error',
      message: body.message ?? response.statusText,
      error: body.error,
      errors: body.errors,
      errorCode: body.errorCode,
      retryAfterSeconds: body.retryAfterSeconds,
    });
  }

  return data as T;
}

export interface ApiClientOptions {
  onUnauthorized?: () => void;
}

export function createApiClient(storageKey: string, options?: ApiClientOptions) {
  const getAuthToken = createGetAuthToken(storageKey);

  const wrappedRequest = <T>(endpoint: string, requestOptions: ApiRequestOptions = {}): Promise<T> =>
    request<T>(endpoint, getAuthToken, requestOptions).catch((error: unknown) => {
      if (error instanceof ApiError && error.statusCode === 401 && options?.onUnauthorized) {
        options.onUnauthorized();
      }
      throw error;
    });

  return {
    get: <T>(endpoint: string, requestOptions?: ApiRequestOptions) =>
      wrappedRequest<T>(endpoint, { ...requestOptions, method: 'GET' }),

    post: <T>(endpoint: string, body?: unknown, requestOptions?: ApiRequestOptions) =>
      wrappedRequest<T>(endpoint, {
        ...requestOptions,
        method: 'POST',
        body: body ? JSON.stringify(body) : undefined,
      }),

    put: <T>(endpoint: string, body?: unknown, requestOptions?: ApiRequestOptions) =>
      wrappedRequest<T>(endpoint, {
        ...requestOptions,
        method: 'PUT',
        body: body ? JSON.stringify(body) : undefined,
      }),

    delete: <T>(endpoint: string, requestOptions?: ApiRequestOptions) =>
      wrappedRequest<T>(endpoint, { ...requestOptions, method: 'DELETE' }),
  };
}
